import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

const adminCountFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 })

export default class extends Controller {
  static targets = [
    "date", "dateButton", "calendarButton", "allDay", "partial", "slots", "affected",
    "zone", "conflictPanel", "conflictCount", "keepChoice", "cancelChoice", "cancelList",
    "submit", "slotEmpty"
  ]

  static values = {
    dates: Object,
    selectedDate: String,
    minDate: String,
    maxDate: String
  }

  connect() {
    this.conflictAction = "keep"
    this.picker = flatpickr(this.dateTarget, {
      clickOpens: false,
      dateFormat: "Y-m-d",
      defaultDate: this.selectedDateValue,
      disableMobile: true,
      locale: { firstDayOfWeek: 1 },
      minDate: this.minDateValue,
      maxDate: this.maxDateValue,
      positionElement: this.calendarButtonTarget,
      onChange: (_dates, dateString) => this.selectDate(dateString, false)
    })
    this.selectDate(this.selectedDateValue, true)
  }

  disconnect() {
    this.picker?.destroy()
  }

  openCalendar(event) {
    event.preventDefault()
    this.picker?.open()
  }

  selectQuick(event) {
    event.preventDefault()
    this.selectDate(event.currentTarget.dataset.date, true)
  }

  selectDate(date, syncPicker = true) {
    if (!date || !this.datesValue[date]) return

    this.selectedDateValue = date
    this.dateTarget.value = date
    if (syncPicker) this.picker?.setDate(date, false)
    this.conflictAction = "keep"
    this.updateDateButtons()
    this.render()
  }

  toggleFullDay() {
    this.render()
  }

  slotChanged() {
    this.renderSummary()
  }

  keepMeetings(event) {
    event.preventDefault()
    this.conflictAction = "keep"
    this.renderConflicts(this.activeConflicts())
  }

  cancelMeetings(event) {
    event.preventDefault()
    this.conflictAction = "cancel"
    this.renderConflicts(this.activeConflicts())
  }

  render() {
    const inventory = this.currentInventory()
    this.zoneTarget.textContent = inventory.zone || "ET"
    this.partialTarget.hidden = this.allDayTarget.checked
    this.renderSlots(inventory.slots || [])
    this.renderSummary()
  }

  renderSlots(slots) {
    this.slotsTarget.replaceChildren()
    const selectable = slots.filter((slot) => !slot.blocked)
    this.slotEmptyTarget.hidden = selectable.length > 0

    slots.forEach((slot) => {
      const label = document.createElement("label")
      label.className = `admin-block-slot${slot.blocked ? " is-disabled" : ""}`

      const input = document.createElement("input")
      input.type = "checkbox"
      input.name = "debbie_strategy_call_block[slot_starts][]"
      input.value = slot.start
      input.checked = !slot.blocked
      input.disabled = slot.blocked
      input.addEventListener("change", () => this.slotChanged())

      const copy = document.createElement("span")
      copy.textContent = slot.blocked ? `${slot.label} · Already blocked` : slot.label
      label.append(input, copy)
      this.slotsTarget.append(label)
    })
  }

  renderSummary() {
    const slots = this.selectedSlots()
    this.affectedTarget.textContent = adminCountFormatter.format(slots.length)
    const conflicts = this.activeConflicts()
    this.renderConflicts(conflicts)

    const noPartialSlots = !this.allDayTarget.checked && slots.length === 0
    this.submitTarget.disabled = noPartialSlots
    this.submitTarget.textContent = noPartialSlots ? "Choose a time slot" : this.submitLabel(conflicts)
  }

  renderConflicts(conflicts) {
    this.conflictPanelTarget.hidden = conflicts.length === 0
    this.conflictCountTarget.textContent = adminCountFormatter.format(conflicts.length)
    this.keepChoiceTarget.classList.toggle("is-selected", this.conflictAction === "keep")
    this.cancelChoiceTarget.classList.toggle("is-selected", this.conflictAction === "cancel")
    this.cancelListTarget.hidden = this.conflictAction !== "cancel"
    this.cancelListTarget.replaceChildren()

    if (this.conflictAction === "cancel") {
      conflicts.forEach((conflict) => this.cancelListTarget.append(this.conflictRow(conflict)))
    }

    this.submitTarget.textContent = this.submitLabel(conflicts)
  }

  conflictRow(conflict) {
    const label = document.createElement("label")
    label.className = "admin-block-conflict-row"

    const input = document.createElement("input")
    input.type = "checkbox"
    input.name = "debbie_strategy_call_block[cancel_call_ids][]"
    input.value = conflict.id
    input.checked = true

    const copy = document.createElement("span")
    const charity = document.createElement("strong")
    charity.textContent = conflict.charity
    const detail = document.createElement("small")
    detail.textContent = `${conflict.label} · ${conflict.contact}`
    copy.append(charity, detail)
    label.append(input, copy)
    return label
  }

  selectedSlots() {
    const slots = this.currentInventory().slots || []
    if (this.allDayTarget.checked) return slots.filter((slot) => !slot.blocked)

    const checked = new Set(
      Array.from(this.slotsTarget.querySelectorAll("input:checked")).map((input) => input.value)
    )
    return slots.filter((slot) => checked.has(slot.start))
  }

  activeConflicts() {
    const conflicts = this.currentInventory().conflicts || []
    if (this.allDayTarget.checked) return conflicts

    const selected = this.selectedSlots()
    return conflicts.filter((conflict) => selected.some((slot) => {
      const callStart = new Date(conflict.starts_at).getTime()
      const callEnd = new Date(conflict.ends_at).getTime()
      return new Date(slot.starts_at).getTime() < callEnd && new Date(slot.ends_at).getTime() > callStart
    }))
  }

  currentInventory() {
    return this.datesValue[this.selectedDateValue] || { slots: [], conflicts: [], zone: "ET" }
  }

  submitLabel(conflicts) {
    if (conflicts.length === 0) return "Block time"
    return this.conflictAction === "cancel" ? "Cancel meetings & block time" : "Block remaining availability"
  }

  updateDateButtons() {
    let quickDateSelected = false
    this.dateButtonTargets.forEach((button) => {
      const selected = button.dataset.date === this.selectedDateValue
      button.classList.toggle("is-selected", selected)
      quickDateSelected ||= selected
    })
    this.calendarButtonTarget.classList.toggle("is-selected", !quickDateSelected)
    this.calendarButtonTarget.setAttribute("aria-label", `Choose another date. Selected ${this.selectedDateValue}`)
  }
}
